import { useState } from "react";
import { Link } from "react-router-dom";
import { Paths } from "src/paths";
import { useCurrentPath, withReturnTo } from "src/shared/utils/returnTo";
import type { PopulationSegment } from "@models/populationSegment.model";
import type {
  EngagementSegmentLinks,
  SegmentLinkJourneyPath,
  SegmentLinks,
} from "@services/engagements/commands/cmdGetSegmentLinks";
import { EngagementService } from "@services/engagements/engagementService";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService";
import LinkEntityModal from "./LinkEntityModal";

type Props = {
  segment: PopulationSegment;
  links: SegmentLinks | null;
  allLinks: EngagementSegmentLinks | null;
  /** A link was written — the host refetches and re-derives everything. */
  onChanged: () => void;
};

const pricesheetHref = (path: SegmentLinkJourneyPath, from: string) =>
  withReturnTo(
    `${import.meta.env.BASE_URL}${Paths.CONTRACT_PRICESHEET}`
      .replace(":contractId", String(path.contract_id))
      .replace(":pricesheetId", String(path.pricesheet_id)),
    from,
  );

/*
    The population's three spokes (MPN-1483): what feeds it (data specs), what
    bills it (pricing groups), what serves it (campaigns). Diagnosis-first:
    healthy links are compact read-only rows; only the gaps carry actions.
    Attach-only by design — moving an entity OFF another population is done
    from that entity's own page, where the change warning lives.
*/
export default function PopulationLinksPanel({ segment, links, allLinks, onChanged }: Props) {
  const here = useCurrentPath();
  const [modal, setModal] = useState<"spec" | "campaign" | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!links || !allLinks) {
    return (
      <div className="data-file__note">
        Link details could not be loaded for this population.
      </div>
    );
  }

  const orphans = allLinks.orphans;
  const billingVisible = links.journey_paths !== null;
  const scopedPaths = links.journey_paths ?? [];
  const billingCovered = scopedPaths.length > 0;
  const otherEngagementCount = links.launch_count_total - links.launches.length;

  const link = async (kind: "spec" | "campaign", entityId: number) => {
    setIsSaving(true);
    setError(null);
    try {
      if (kind === "campaign") {
        await new EngagementService().setLaunchPopulationSegment(entityId, segment.id);
      } else {
        await new IngestDefinitionService().setPopulationSegment(entityId, segment.id);
      }
      setModal(null);
      onChanged();
    } catch {
      setError("Could not save the link. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const section = (
    icon: string,
    title: string,
    children: React.ReactNode,
    action?: React.ReactNode,
  ) => (
    <div className="data-file__section mb-3">
      <div className="data-file__head">
        <i className={`bi ${icon}`} style={{ color: "#64748b" }} />
        <span className="data-file__title">{title}</span>
        {action && <div className="ms-auto">{action}</div>}
      </div>
      <div className="data-file__body">{children}</div>
    </div>
  );

  const itemRow = (key: string, icon: string, body: React.ReactNode) => (
    <div key={key} className="d-flex align-items-center gap-2 py-1 small">
      <i className={`bi ${icon}`} style={{ color: "#10b981" }} />
      <div>{body}</div>
    </div>
  );

  const gapRow = (body: React.ReactNode, severity: "warn" | "info") => (
    <div
      className={`d-flex align-items-center gap-2 py-1 small ${
        severity === "warn" ? "text-warning-emphasis" : "text-muted"
      }`}
    >
      <i className={`bi ${severity === "warn" ? "bi-exclamation-triangle-fill" : "bi-info-circle"}`} />
      <div>{body}</div>
    </div>
  );

  return (
    <div>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {section(
        "bi-database-down",
        "Data in",
        links.ingest_definitions.length > 0
          ? links.ingest_definitions.map((d) => itemRow(`def-${d.id}`, "bi-check-circle-fill", d.name))
          : gapRow(
              <>No data spec feeds this population — no data will arrive.</>,
              "warn",
            ),
        links.ingest_definitions.length === 0 && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: "0.72rem" }}
            disabled={orphans.ingest_definitions_without_segment.length === 0}
            title={
              orphans.ingest_definitions_without_segment.length === 0
                ? "No unlinked data specs — create one on the Data Spec tab."
                : undefined
            }
            onClick={() => setModal("spec")}
          >
            Link a data spec
          </button>
        ),
      )}

      {billingVisible &&
        section(
          "bi-currency-dollar",
          "Billing",
          <>
            {scopedPaths.map((p) =>
              itemRow(
                `path-${p.id}`,
                "bi-check-circle-fill",
                <>
                  {p.name || `Pricing group #${p.id}`}
                  {p.contract_name ? ` — ${p.contract_name}` : ""}{" "}
                  <Link to={pricesheetHref(p, here)} className="ms-1">
                    Open pricesheet
                  </Link>
                </>,
              ),
            )}
            {!billingCovered &&
              gapRow(
                <>
                  No pricing group bills this population — conversions here won't be
                  invoiced. Add one for this population on the contract's pricesheet.
                </>,
                "warn",
              )}
          </>,
        )}

      {section(
        "bi-broadcast",
        "Serving",
        <>
          {links.launches.map((l) => itemRow(`launch-${l.id}`, "bi-check-circle-fill", l.name))}
          {otherEngagementCount > 0 && (
            <div className="population-card__muted py-1">
              +{otherEngagementCount} campaign{otherEngagementCount === 1 ? "" : "s"} in other
              engagements target{otherEngagementCount === 1 ? "s" : ""} this population.
            </div>
          )}
          {links.launch_count_total === 0 &&
            gapRow(<>No campaign targets this population yet.</>, "info")}
        </>,
        links.launches.length === 0 && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: "0.72rem" }}
            disabled={orphans.launches_without_segment.length === 0}
            title={
              orphans.launches_without_segment.length === 0
                ? "Every campaign in this engagement already has a population."
                : undefined
            }
            onClick={() => setModal("campaign")}
          >
            Link a campaign
          </button>
        ),
      )}

      {modal === "spec" && (
        <LinkEntityModal
          title="Link a data spec"
          segmentName={segment.name}
          candidates={orphans.ingest_definitions_without_segment}
          isSaving={isSaving}
          onClose={() => setModal(null)}
          onLink={(id) => void link("spec", id)}
        />
      )}
      {modal === "campaign" && (
        <LinkEntityModal
          title="Link a campaign"
          segmentName={segment.name}
          candidates={orphans.launches_without_segment}
          isSaving={isSaving}
          onClose={() => setModal(null)}
          onLink={(id) => void link("campaign", id)}
        />
      )}
    </div>
  );
}
